import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '../../../axiosInstances/axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { Client } from '../../../types/users';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useClientActions = (
  whichAction: 'clientListHandler' | 'clientListById' | '',
  associateId?: number,
  clientId?: number
) => {
  // fetching the client list here
  const clientListHandler = useQuery({
    queryKey: ['client-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Client>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/client/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'clientListHandler'),
  });

  // fetching client details here
  const getClientDetailsHandler = useQuery({
    queryKey: ['client-details', clientId],
    queryFn: async (): Promise<AcademiaResponse<Client>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/client/${clientId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!clientId),
  });

  // query function to get client list by associate id declared here
  const clientListByAssociateIdHandler = useQuery({
    queryKey: ['associate-client-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Client>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        `/associate/clients/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'clientListById' && !!associateId),
  });

  // mutation function to create new client declared here
  const createNewClientHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: FormData;
    }): Promise<AcademiaResponse<Client>> => {
      const response = await getAxiosInstance('user').post(
        '/client/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update existing client using id declared here
  const updateClientHandler = useMutation({
    mutationFn: async ({
      clientId,
      body,
    }: {
      clientId: number;
      body: FormData;
    }): Promise<AcademiaResponse<Client>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/client/${clientId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to change client password declared here
  const changeClientPasswordHandler = useMutation({
    mutationFn: async ({
      clientId,
      body,
    }: {
      clientId: number;
      body: { password: string };
    }): Promise<AcademiaResponse<Client>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/client/${clientId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // toggling client active/deactive status mutation function declared here
  const toggleClientActivityHandler = useMutation({
    mutationFn: async ({
      clientId,
      body,
    }: {
      clientId: number;
      body: { is_active: boolean };
    }): Promise<AcademiaResponse<Client>> => {
      const response = await getPrivateAxiosInstance('user').patch(
        `/client/${clientId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete existing client by id declared here
  const deleteClientByIdHandler = useMutation({
    mutationFn: ({
      clientId,
    }: {
      clientId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/client/${clientId}/delete/`),
  });

  return {
    clientListHandler,
    getClientDetailsHandler,
    clientListByAssociateIdHandler,
    createNewClientHandler,
    updateClientHandler,
    changeClientPasswordHandler,
    deleteClientByIdHandler,
    toggleClientActivityHandler,
  };
};

export default useClientActions;
